import { repairModelJsonText, ModelResponseFormatError } from "./model-json";

const MAX_INPUT = 128_000;
const TEXT_KEYS = ["body", "narration", "text", "content", "story", "reply", "answer", "response", "output_text", "narrative", "host_reply", "正文", "剧情", "旁白"];
const LIST_KEYS = ["phoneMessages", "paragraphs", "scene_beats", "sceneBeats", "messages", "lines", "dialogue", "dialogues", "segments", "beats", "novel_paragraphs", "speakerLines"];
type RecordValue = Record<string, unknown>;

/** Text is rendered as text, never HTML. Do not expose model reasoning or protocol debris. */
export function cleanNarrativeText(value: unknown, max = 12_000): string {
  if (typeof value !== "string") return "";
  return value.replace(/<think\b[^>]*>[\s\S]*?(?:<\/think>|$)/gi, "")
    .replace(/<script\b[^>]*>[\s\S]*?(?:<\/script>|$)/gi, "")
    .replace(/<[^>]*>/g, "").replace(/^```(?:json|text|markdown)?\s*/i, "").replace(/\s*```$/, "")
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "").trim().slice(0, max);
}

function stripped(raw: string) {
  return raw.slice(0, MAX_INPUT).replace(/<think\b[^>]*>[\s\S]*?(?:<\/think>|$)/gi, "")
    .trim().replace(/^```(?:json|text|markdown)?\s*/i, "").replace(/\s*```$/, "").trim();
}

/** Complete JSON only. Wrappers/fences/double encoding are accepted; state is never invented. */
export function parseModelJson(raw: string): unknown {
  let text = stripped(raw);
  for (let depth = 0; depth < 4; depth++) {
    try {
      const value: unknown = JSON.parse(repairModelJsonText(text));
      if (typeof value !== "string") return value;
      text = stripped(value);
    } catch { break; }
  }
  // Balanced first object/array, not a greedy slice across two independent documents.
  let quoted = false, escaped = false, start = -1;
  const stack: string[] = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) { if (escaped) escaped = false; else if (c === "\\") escaped = true; else if (c === '"') quoted = false; continue; }
    if (c === '"' && start >= 0) { quoted = true; continue; }
    if (c === "{" || c === "[") { if (start < 0) start = i; stack.push(c); }
    if (start >= 0 && (c === "}" || c === "]")) {
      if (stack.pop() !== (c === "}" ? "{" : "[")) break;
      if (!stack.length) { try { return JSON.parse(repairModelJsonText(text.slice(start, i + 1))); } catch { break; } }
    }
  }
  throw new ModelResponseFormatError();
}

function isEnvelope(value: RecordValue) {
  return "error" in value || ("code" in value && ("message" in value || "data" in value)) || "choices" in value && "usage" in value;
}

function readText(value: unknown, depth = 0): string {
  if (depth > 5) return "";
  if (typeof value === "string") {
    const text = cleanNarrativeText(value);
    if (/^[{\[]/.test(text)) { try { return readText(parseModelJson(text), depth + 1); } catch { return ""; } }
    return text;
  }
  if (Array.isArray(value)) return value.slice(0, 32).map(v => readText(v, depth + 1)).filter(Boolean).join("\n\n");
  if (!value || typeof value !== "object") return "";
  const record = value as RecordValue;
  if (isEnvelope(record)) return "";
  for (const key of TEXT_KEYS) { const text = readText(record[key], depth + 1); if (text) return text; }
  for (const key of LIST_KEYS) { const text = readText(record[key], depth + 1); if (text) return text; }
  for (const key of ["result", "data", "message", "output", "narrative"]) { const text = readText(record[key], depth + 1); if (text) return text; }
  return "";
}

/** Recover ONLY completed top-level text values from truncated JSON. Never recover numeric/state fields. */
function partialText(text: string): string {
  if (!text.startsWith("{")) return "";
  if (/"(?:error|code|usage)"\s*:/.test(text)) return "";
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === "{" || c === "[") depth++;
    else if (c === "}" || c === "]") depth--;
    else if (c === '"') {
      const start = i++; let escaped = false;
      for (; i < text.length; i++) { if (escaped) escaped = false; else if (text[i] === "\\") escaped = true; else if (text[i] === '"') break; }
      if (i >= text.length) return "";
      let key: unknown; try { key = JSON.parse(text.slice(start, i + 1)); } catch { return ""; }
      if (depth !== 1 || typeof key !== "string") continue;
      const tail = text.slice(i + 1), match = /^\s*:\s*("(?:[^"\\]|\\.)*")/.exec(tail);
      if (["error", "code", "usage"].includes(key)) return "";
      if (TEXT_KEYS.includes(key) && match) { try { return readText(JSON.parse(match[1])); } catch { return ""; } }
    }
  }
  return "";
}

export function extractNarrativeText(raw: string): string {
  const text = stripped(raw);
  if (!text) return "";
  try { return readText(parseModelJson(text)); } catch {
    // Incomplete protocol must not leak through the ordinary prose path.
    if (/^[{\[]\s*(?:["{\]}]|$)/.test(text) || /["'](?:body|text|narration|content)["']\s*:/.test(text)) return partialText(text);
    return cleanNarrativeText(text);
  }
}

/** Opt-in presentation adapter. Missing optional fields stay absent; explicit IDs/state stay untouched. */
export function readNarrativeRecord(raw: string): RecordValue {
  let record: RecordValue = {};
  try {
    const value = parseModelJson(raw);
    if (value && typeof value === "object" && !Array.isArray(value)) record = value as RecordValue;
    for (let depth = 0; depth < 4; depth++) {
      if (TEXT_KEYS.some(key => typeof record[key] === "string") || LIST_KEYS.some(key => Array.isArray(record[key]))) break;
      const key = ["result", "data", "message", "output", "narrative"].find(key => record[key] && typeof record[key] === "object" && !Array.isArray(record[key]));
      if (!key || isEnvelope(record)) break;
      record = record[key] as RecordValue;
    }
    if (isEnvelope(record)) throw new ModelResponseFormatError();
  } catch { /* only readable narrative below is eligible for presentation */ }
  const text = extractNarrativeText(raw);
  if (!text) throw new ModelResponseFormatError();
  // Do not replace explicitly present structured dialogue or beats.
  const result = { ...record };
  for (const key of TEXT_KEYS) if (result[key] === undefined || typeof result[key] === "string" && !result[key].trim()) result[key] = text;
  if (result.paragraphs === undefined) result.paragraphs = text.split(/\n\s*\n/).filter(Boolean);
  if (result.novel_paragraphs === undefined) result.novel_paragraphs = result.paragraphs;
  return result;
}

/** Shared transport fallback only preserves narrative; malformed machine state remains unusable. */
export function normalizeNarrativeResponse(raw: string): string {
  try { return JSON.stringify(parseModelJson(raw)); } catch {
    const text = extractNarrativeText(raw);
    if (!text) throw new ModelResponseFormatError();
    // Plain prose remains prose, so existing text-only games keep their presentation.
    if (!stripped(raw).startsWith("{")) return text;
    return JSON.stringify({ body: text, narration: text, text, content: text, narrativeOnly: true });
  }
}
